import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const endpointMap: Record<string, string> = {
  mystery_score: 'mysteryscore',
  mystery_series: 'mysteryseries',
  clean_game: 'cleangame',
  survivor: 'survivor',
  eliminator: 'eliminator',
  side_pots: 'sidepot',
  high_game: 'highgame',
  high_series: 'highseries',
};

export interface BookiePayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  bowlerId: number;
  bowlerName: string;
  sideEventId: number;
  sideEventType: 'mystery_score' | 'mystery_series' | 'clean_game' | 'survivor' | 'eliminator' | 'side_pots' | 'high_game' | 'high_series';
  leagueId: number;
  defaultAmount: number;
  onPayoutComplete?: () => void;
}

const BookiePayoutModal: React.FC<BookiePayoutModalProps> = ({
  isOpen,
  onClose,
  bowlerId,
  bowlerName,
  sideEventId,
  sideEventType,
  leagueId,
  defaultAmount,
  onPayoutComplete,
}) => {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setAmount(defaultAmount > 0 ? defaultAmount.toFixed(2) : '');
      setNotes('');
      setSuccess(false);
      setError(null);
    }
  }, [isOpen, defaultAmount]);

  if (!isOpen) return null;

  const parsedAmount = parseFloat(amount);
  const canSubmit = !processing && !isNaN(parsedAmount) && parsedAmount > 0;

  const handlePayout = async () => {
    if (!canSubmit) return;
    setProcessing(true);
    setError(null);
    try {
      await axios.post(
        `${API}/admin/payouts/${endpointMap[sideEventType]}`,
        {
          sideEventId,
          leagueId,
          recipientType: 'bowler',
          recipientId: bowlerId,
          amount: parsedAmount,
          sideEventType: 'side_event',
          placement: 1,
          notes: notes.trim(),
        },
        { headers: authHeader() }
      );
      setSuccess(true);
      onPayoutComplete?.();
      setTimeout(onClose, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Payout failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const typeLabel: Record<string, string> = {
    mystery_score: 'Mystery Score',
    mystery_series: 'Mystery Series',
    clean_game: 'Clean Game',
    survivor: 'Survivor',
    eliminator: 'Eliminator',
    side_pots: 'Side Pot',
    high_game: 'High Game',
    high_series: 'High Series',
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={e => { if (e.target === e.currentTarget && !processing) onClose(); }}
    >
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">Record Payout</h2>
          <button
            onClick={onClose}
            disabled={processing}
            className="text-gray-400 hover:text-white transition-colors p-1 disabled:opacity-40"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {success ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-3">✅</div>
            <p className="text-green-400 font-semibold text-lg">Payout recorded!</p>
            <p className="text-gray-300 text-sm mt-1">
              ${parsedAmount.toFixed(2)} → <span className="font-medium text-white">{bowlerName}</span>
            </p>
          </div>
        ) : (
          <>
            {/* Bowler card */}
            <div className="flex items-center gap-3 mb-5 p-3 bg-gray-700/50 rounded-lg border border-gray-600/50">
              <div className="w-10 h-10 rounded-full bg-purple-600/60 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                {bowlerName.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-white font-semibold text-sm">{bowlerName}</div>
                <div className="text-gray-400 text-xs">{typeLabel[sideEventType]} winner</div>
              </div>
            </div>

            {/* Amount */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Payout Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="0.00"
                  autoFocus
                  className="w-full pl-7 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 placeholder-gray-500"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Notes <span className="text-gray-600">(optional)</span></label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                placeholder="e.g. Game 3, Squad 1…"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 resize-none placeholder-gray-500"
              />
            </div>

            {error && (
              <div className="mb-4 px-3 py-2 bg-red-900/40 border border-red-500/40 rounded-lg text-red-300 text-xs">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={processing}
                className="flex-1 px-4 py-2.5 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePayout}
                disabled={!canSubmit}
                className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing…</>
                  : <><span>💰</span> Record Payout</>
                }
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookiePayoutModal;
